/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { BasePublication } from '@elio/emanager-front/publication/model';
import {
  DayOfMonth,
  LocalDate,
  LocalDateTime,
  LocalTime,
  Month,
  TimeUnit,
  buildLocalDateTime,
  buildLocalTime,
} from '@elio/shared/models';

export const SERIALIZATION_FORMAT = 'yyyy-MM-dd';
export const SERIALIZATION_FORMAT2 = 'dd/MM/yyyy';
export const MINUTE_IN_MS = 60000;
export const HOURS_IN_MS = MINUTE_IN_MS * 60;
export const DAY_IN_MS = HOURS_IN_MS * 24;

export interface TimeAgo {
  timeUnit: string;
  timeValue: number;
}

export function toLocalDate(date: Date | LocalDateTime): LocalDate;
export function toLocalDate(date: null | undefined): undefined;
export function toLocalDate(date: Date | LocalDateTime | undefined | null): LocalDate | undefined {
  if (!date) return undefined;
  else if (isLocalDateTime(date)) {
    date = toDate(date);
  }
  return { year: date.getFullYear(), month: (date.getMonth() + 1) as Month, day: date.getDate() as DayOfMonth };
}

export function toLocalDateTime(date: Date | number): LocalDateTime {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();
  return buildLocalDateTime(year, month, day, hours, minutes, seconds);
}

export function toTimestamp(date: Date): number {
  return date.getTime();
}

export function toLocalTime(date: LocalDateTime | Date): LocalTime {
  if (isLocalDateTime(date)) {
    return buildLocalTime(date.hour, date.minute);
  }
  return buildLocalTime(date.getHours(), date.getMinutes());
}

export function toDate(localDateOrDateTime: LocalDate | LocalDateTime): Date;
export function toDate(localDateOrDateTime?: LocalDate | LocalDateTime | null): Date | null;
export function toDate(localDateOrDateTime?: LocalDate | LocalDateTime | null): Date | null {
  if (!localDateOrDateTime) {
    return null;
  }
  const result = new Date(localDateOrDateTime.year, localDateOrDateTime.month - 1, localDateOrDateTime.day);
  if (isLocalDateTime(localDateOrDateTime)) {
    result.setHours(localDateOrDateTime.hour, localDateOrDateTime.minute, localDateOrDateTime.second, 0);
  } else {
    result.setHours(0, 0, 0, 0);
  }
  return result;
}

export const isPast = (date?: Date | LocalDate | LocalDateTime | null): boolean => {
  if (date) {
    const now = new Date();
    let time: number;
    if (isLocalDateTime(date)) {
      time = toDate(date).getTime();
    } else if (isLocalDate(date)) {
      const today = toLocalDate(now);
      return compareLocaleDates(today, date) > 0;
    } else {
      time = date.getTime();
    }
    return time < now.getTime();
  }
  return false;
};

export const isFuture = (date?: Date | LocalDate | LocalDateTime): boolean => {
  if (date) {
    const now = new Date();
    if (isLocalDate(date)) {
      const today = toLocalDate(now);
      return compareLocaleDates(today, date) < 0;
    }
    let time: number;
    if (isLocalDateTime(date)) {
      time = toDate(date).getTime();
    } else {
      time = date.getTime();
    }
    return time > Date.now();
  }
  return false;
};

export const isToday = (date?: Date | LocalDate | LocalDateTime): boolean => {
  if (date) {
    const today = toLocalDate(new Date());
    return isEqual(today, isLocalDate(date) && !isLocalDateTime(date) ? date : toLocalDate(date));
  }
  return false;
};

/**
 * date can be undefined and should return false in that case
 */
export function isEqual(date1: LocalDate, date2?: LocalDate): boolean;
export function isEqual(date1: LocalDateTime, date2?: LocalDateTime): boolean;
export function isEqual(date1: Date, date2?: Date): boolean;
export function isEqual(date1: Date | LocalDate | LocalDateTime, date2?: Date | LocalDate | LocalDateTime): boolean {
  if (!date2) {
    return false;
  }
  let timestamp1, timestamp2;
  if ((isLocalDate(date1) && isLocalDate(date2)) || (isLocalDateTime(date1) && isLocalDateTime(date2))) {
    timestamp1 = toDate(date1).getTime();
    timestamp2 = toDate(date2).getTime();
  } else {
    timestamp1 = (date1 as Date).getTime();
    timestamp2 = (date2 as Date).getTime();
  }
  return timestamp1 === timestamp2;
}

export const isTodayBetweenOrAfter = ({ startDate, endDate }: { startDate?: Date; endDate?: Date }): boolean => {
  if (startDate && endDate) {
    return isTodayBetween({ startDate, endDate });
  }
  if (startDate && !endDate) {
    return isTodayAfter(startDate);
  }
  return false;
};

export const isTodayBetween = ({ startDate, endDate }: { startDate?: Date; endDate?: Date }): boolean => {
  if (startDate && endDate) {
    const today = Date.now();
    return startDate.getTime() < today && today < endDate.getTime();
  }
  return false;
};

/** Check if @param dateTime is more recent than today minus @param nbDaysFromNow  */
export const isDateMoreRecentThan = ({
  dateTime,
  nbDaysFromNow = 3,
}: {
  dateTime: LocalDateTime;
  nbDaysFromNow?: number;
}): boolean => {
  const recentThan = Date.now() - nbDaysFromNow * DAY_IN_MS;
  return toDate(dateTime).getTime() > recentThan;
};

export const isTodayAfter = (startDate: Date): boolean => {
  return startDate.getTime() < Date.now();
};

export function getTodayLocalDate(): LocalDate {
  return toLocalDate(new Date());
}

export function getNowLocalDateTime(): LocalDateTime {
  return toLocalDateTime(new Date());
}

export const yesterday = (): Date => {
  return addXDaysToDate(new Date(), -1);
};

export const tomorrow = (): Date => {
  return addXDaysToDate(new Date(), 1);
};

function modulo(n: number, m: number): number {
  const remain = n % m;
  return remain < 0 ? remain + m : remain;
}

export const addMinutes = (time: LocalTime, minutes: number): LocalTime => {
  const minutesSum = time.minute + minutes;
  const newHour = modulo(time.hour + Math.floor(minutesSum / 60), 24);
  const newMinute = modulo(minutesSum, 60);
  return buildLocalTime(newHour, newMinute);
};

export const newDateInXMinutes = (minutes: number): Date => {
  return new Date(Date.now() + minutes * MINUTE_IN_MS);
};

export const addXDaysToDate = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + days);
  return newDate;
};

export const addXMonthsToDate = (date: Date, months: number): Date => {
  const newDate = new Date(date);
  newDate.setMonth(date.getMonth() + months);
  return newDate;
};

export const getAge = (bday: LocalDate | LocalDateTime): number => {
  const timeDiffInMs = Math.abs(Date.now() - toDate(bday).getTime());
  return Math.floor(timeDiffInMs / DAY_IN_MS / 365);
};

export const getAgeDateType = (bday: Date): number => {
  const timeDiffInMs = Math.abs(Date.now() - bday.getTime());
  return Math.floor(timeDiffInMs / DAY_IN_MS / 365);
};

// LocalDate is not a valid type for `dateOrTime` as is does not handle any concept of time
export const getAbsTimeSinceTodayInMs = (dateOrTime: number | LocalDateTime | Date): number => {
  let dateInMs;
  if (isLocalDateTime(dateOrTime)) {
    dateInMs = toDate(dateOrTime).getTime();
  } else if (dateOrTime instanceof Date) {
    dateInMs = dateOrTime.getTime();
  } else {
    dateInMs = dateOrTime; // already in ms
  }
  return Math.abs(Date.now() - dateInMs);
};

export const getAbsTimeSinceToday = (
  dateOrTime: number | LocalDate | LocalDateTime | Date
): { timeValue: number; timeUnit: TimeUnit } => {
  if (isLocalDate(dateOrTime) && !isLocalDateTime(dateOrTime)) {
    const today = toDate(toLocalDate(new Date())); // ensure we compare date without time
    return {
      timeValue: Math.floor(Math.abs((today.getTime() - toDate(dateOrTime).getTime()) / DAY_IN_MS)),
      timeUnit: 'days',
    };
  }

  const elapsedTimeInMs = getAbsTimeSinceTodayInMs(dateOrTime);
  if (elapsedTimeInMs < HOURS_IN_MS) {
    return { timeValue: Math.round(elapsedTimeInMs / MINUTE_IN_MS), timeUnit: 'minutes' };
  } else if (elapsedTimeInMs >= HOURS_IN_MS && elapsedTimeInMs < DAY_IN_MS) {
    return { timeValue: Math.round(elapsedTimeInMs / HOURS_IN_MS), timeUnit: 'hours' };
  } else {
    // elapsedTimeInMs >= DAY_IN_MS
    return {
      timeValue: Math.round(elapsedTimeInMs / DAY_IN_MS),
      timeUnit: 'days',
    };
  }
};

export const isLocalDate = (obj: unknown): obj is LocalDate =>
  typeof obj === 'object' && obj != null && 'year' in obj && 'month' in obj && 'day' in obj;

export const isLocalTime = (obj: unknown): obj is LocalTime =>
  typeof obj === 'object' && obj != null && 'hour' in obj && 'minute' in obj;

export const isLocalDateTime = (obj: unknown): obj is LocalDateTime =>
  typeof obj === 'object' && obj != null && isLocalDate(obj) && isLocalTime(obj) && 'second' in obj;

/**
 * compare date1 and date2 then returns :
 * -1 if date1 < date2 (date1 is older than date2)
 * 1 if date1 > date2 (date1 is newer than date2)
 * 0 if date1 = date2
 */
export const compareLocaleDates = (localDate1: LocalDate, localDate2: LocalDate): number => {
  const date1 = toDate(localDate1);
  const date2 = toDate(localDate2);
  if (date1.getTime() === date2.getTime()) return 0;
  return date1.getTime() - date2.getTime();
};

export const compareLocaleDateTimes = (localDate1: LocalDateTime, localDate2: LocalDateTime): number => {
  const date1 = toDate(localDate1);
  const date2 = toDate(localDate2);
  if (date1.getTime() === date2.getTime()) return 0;
  return date1.getTime() - date2.getTime();
};

export const compareDateTimes = (dateOrTime1: Date, dateOrTime2: Date): number => {
  if (dateOrTime1.getTime() === dateOrTime2.getTime()) return 0;

  return dateOrTime1.getTime() - dateOrTime2.getTime();
};

/**
 * Comparison function for LocalTime.
 */
export const compareLocalTimes = (localTime1: LocalTime, localTime2: LocalTime): number => {
  const hourComparison = localTime1.hour - localTime2.hour;
  if (hourComparison !== 0) return hourComparison;
  return localTime1.minute - localTime2.minute;
};

/**
 * compare time1 from time2: time1 must be BEFORE time2
 */
export const isLocalTimeBefore = (time1: LocalTime, time2: LocalTime): boolean => {
  return compareLocalTimes(time1, time2) < 0;
};

/**
 * compare time1 from time2: time1 must be AFTER time2
 */
export const isLocalTimeAfter = (time1: LocalTime, time2: LocalTime): boolean => {
  return compareLocalTimes(time1, time2) > 0;
};

export const isLocalTimeEqual = (time1: LocalTime, time2: LocalTime): boolean => {
  return compareLocalTimes(time1, time2) === 0;
};

export const getPublicationStatusByDate = (p: BasePublication): 'IN_PROGRESS' | 'DONE' | 'FUTURE' => {
  if (isFuture(toDate(p.dateBegin))) {
    return 'FUTURE';
  } else if (isPast(toDate(p.dateEnd))) {
    return 'DONE';
  } else {
    return 'IN_PROGRESS';
  }
};
